import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PrimaryButton, SecondaryLink, StatusBanner } from "../components/ui";
import { adminAppUrl } from "../data/admin";
import { CUSTOMER_CONTACT_EMAIL, CUSTOMER_CONTACT_MAILTO } from "../data/contact";
import { friendlyApiErrorPayload } from "../lib/userFacingErrors";

type Bullet = string | { readonly text: string; readonly info: true };

type Tier = {
	readonly id: "free" | "plus" | "team";
	readonly name: string;
	readonly personality: string;
	readonly price: string;
	readonly tagline: string;
	readonly highlight: boolean;
	readonly bullets: readonly Bullet[];
	readonly ctaLabel: string;
};

const TIERS: readonly Tier[] = [
	{
		id: "free",
		name: "Free",
		personality: "The Universe",
		price: "Free",
		tagline: "For one-off decisions.",
		highlight: false,
		bullets: [
			"No account wall for voters",
			"No accounts or logins",
			"Unlimited public polls",
			"48-hour poll lifespan",
			"Standard Ranked Choice",
		],
		ctaLabel: "Get started free",
	},
	{
		id: "plus",
		name: "Plus",
		personality: "The Supporter",
		price: "Stripe checkout",
		tagline: "For the home team that polls often.",
		highlight: true,
		bullets: [
			"Everything in Free",
			"Up to 10 reusable Supporter Ideas (rolling out)",
			"Custom team slug + Idea slug URLs (rolling out)",
			"Editable Idea prompt and answer templates (rolling out)",
			"Survey expirations from minutes up to 72 hours (rolling out)",
			"Open Ideas, Close Idea, and Reopen Idea admin (rolling out)",
			"“Worthy Human” badge when tier is active",
		],
		ctaLabel: "Upgrade to Plus",
	},
	{
		id: "team",
		name: "Team",
		personality: "The Team",
		price: "Contact us",
		tagline: "For organizations that need governance and SSO.",
		highlight: false,
		bullets: ["Everything in Plus", { text: "Planned: private namespaces (SSO)", info: true }],
		ctaLabel: "Talk to us about Team",
	},
] as const;

const TEAM_ROADMAP = [
	"Hidden Results Mode — interim tallies stay hidden until the Curator closes the poll",
	"Exportable CSV/JSON results",
	"Admin-locked rooms",
] as const;

function TierBullet({ children }: { children: React.ReactNode }) {
	return (
		<li className="flex gap-2.5 text-body-sm text-wi-text/85 leading-snug">
			<span className="mt-2 shrink-0" aria-hidden>
				<svg width="7" height="7" viewBox="0 0 7 7" className="text-wi-gold" fill="none">
					<circle cx="3.5" cy="3.5" r="2.5" stroke="currentColor" strokeWidth="0.85" />
				</svg>
			</span>
			<span>{children}</span>
		</li>
	);
}

function SsoInfo() {
	return (
		<details className="relative ml-1 inline-block align-middle">
			<summary className="inline-flex size-5 cursor-pointer list-none items-center justify-center rounded-full border border-wi-text/18 text-wi-text/55 transition hover:border-wi-gold/45 hover:text-wi-gold [&::-webkit-details-marker]:hidden">
				<span className="sr-only">About private namespaces (SSO)</span>
				<svg aria-hidden width="11" height="11" viewBox="0 0 12 12" className="pointer-events-none">
					<circle cx="6" cy="6" r="5" fill="none" stroke="currentColor" strokeWidth="1" />
					<path d="M6 3.75v0.01M6 5.1v3.35" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
				</svg>
			</summary>
			<div className="wi-card absolute left-1/2 top-full z-30 mt-2 w-[min(16rem,calc(100vw-2rem))] -translate-x-1/2 p-3 text-left text-caption text-wi-text/85 leading-relaxed">
				Microsoft Entra ID (Azure AD) mapping for org-owned namespaces — planned for Team; not active in this build.
			</div>
		</details>
	);
}

function TierCard({
	highlight,
	children,
}: {
	highlight: boolean;
	children: React.ReactNode;
}) {
	return (
		<div
			className={`flex h-full flex-col rounded-wi-card bg-wi-surface p-6 ${
				highlight ? "border-2 border-wi-gold" : "border border-wi-border"
			}`}
		>
			{children}
		</div>
	);
}

function renderBullet(b: Bullet) {
	if (typeof b === "string") {
		return <TierBullet key={b}>{b}</TierBullet>;
	}
	return (
		<TierBullet key={b.text}>
			<span className="inline-flex flex-wrap items-center gap-0.5">
				{b.text}
				{b.info ? <SsoInfo /> : null}
			</span>
		</TierBullet>
	);
}

export function MembershipPage() {
	const [params, setParams] = useSearchParams();
	const sessionId = params.get("session_id");
	const [checkoutBanner, setCheckoutBanner] = useState<{ kind: "success" | "error"; text: string } | null>(null);
	const [checkoutLoading, setCheckoutLoading] = useState(false);

	async function startSupporterCheckout() {
		setCheckoutLoading(true);
		setCheckoutBanner(null);
		try {
			const res = await fetch("/api/billing/checkout", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				credentials: "include",
			});
			let data: unknown = null;
			try {
				data = await res.json();
			} catch {
				data = null;
			}
			const url = data && typeof data === "object" ? (data as { url?: unknown }).url : null;
			if (res.ok && typeof url === "string" && url.startsWith("https://")) {
				window.location.assign(url);
				return;
			}
			setCheckoutBanner({
				kind: "error",
				text: friendlyApiErrorPayload(data, "Stripe checkout is not available until billing env is configured."),
			});
		} catch {
			setCheckoutBanner({
				kind: "error",
				text: "Stripe checkout is not available right now. Check your connection and try again.",
			});
		} finally {
			setCheckoutLoading(false);
		}
	}

	useEffect(() => {
		if (!sessionId || !/^cs_[A-Za-z0-9_]+$/.test(sessionId)) {
			if (sessionId) {
				setParams({}, { replace: true });
				setCheckoutBanner({
					kind: "error",
					text: friendlyApiErrorPayload({ error: "invalid_session" }, "That return link isn’t valid."),
				});
			}
			return;
		}
		let cancelled = false;
		void (async () => {
			try {
				const res = await fetch("/api/billing/verify-session", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					credentials: "include",
					body: JSON.stringify({ sessionId }),
				});
				let data: unknown = null;
				try {
					data = await res.json();
				} catch {
					data = null;
				}
				if (cancelled) return;
				setParams({}, { replace: true });
				const ok = res.ok && data && typeof data === "object" && (data as { ok?: unknown }).ok === true;
				if (ok) {
					window.dispatchEvent(new Event("wi-account-changed"));
					setCheckoutBanner({
						kind: "success",
						text: "Thanks — your Plus plan is active on this browser.",
					});
				} else {
					setCheckoutBanner({
						kind: "error",
						text: friendlyApiErrorPayload(
							data,
							"We couldn’t confirm checkout. Try again from Plans or contact support.",
						),
					});
				}
			} catch {
				if (!cancelled) {
					setParams({}, { replace: true });
					setCheckoutBanner({
						kind: "error",
						text: "We couldn’t confirm checkout. Check your connection and try again.",
					});
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [sessionId, setParams]);

	return (
		<div className="space-y-xl pb-8">
			<header className="wi-editorial-prose space-y-sm">
				<p className="text-caption font-medium text-wi-gold uppercase tracking-[0.2em]">Plans</p>
				<h1 className="wi-type-h1 text-wi-text">Plans</h1>
				<p className="max-w-2xl text-body text-wi-text/78">
					Everyone gets fast, private polls for free. Upgrade when you&apos;re running them again and again.
				</p>
				<StatusBanner variant="pilot" className="max-w-2xl">
					Plus administration for reusable Ideas is rolling out on staging after Stripe test-mode smoke — checkout
					works when billing env is configured; Idea boards follow.
				</StatusBanner>
				{checkoutBanner ? (
					<p
						className={`max-w-2xl rounded-wi-card border px-4 py-3 text-body-sm leading-relaxed ${
							checkoutBanner.kind === "success"
								? "border-wi-signal/40 bg-wi-ink-soft text-wi-text"
								: "border-wi-gold/35 bg-wi-gold-soft text-wi-text"
						}`}
						role={checkoutBanner.kind === "error" ? "alert" : "status"}
					>
						{checkoutBanner.text}
					</p>
				) : null}
			</header>

			<section
				id="membership-tiers"
				className="rounded-wi-card bg-wi-membership-band px-5 py-lg md:px-10 md:py-xl"
				aria-labelledby="membership-tiers-heading"
			>
				<h2 id="membership-tiers-heading" className="sr-only">
					Compare plans
				</h2>

				<div className="mx-auto max-w-6xl">
					<div id="membership-tier-cards" className="grid grid-cols-1 gap-md md:grid-cols-3 md:gap-lg">
						{TIERS.map((tier) => (
							<TierCard key={tier.id} highlight={tier.highlight}>
								<div className="border-wi-text/8 border-b pb-4">
									<h2 className="wi-type-h2 text-wi-text">{tier.name}</h2>
									<p className="mt-1 text-caption text-wi-text/55 italic">{tier.personality}</p>
									<p className="mt-sm font-medium text-body-sm text-wi-gold tracking-wide">{tier.price}</p>
									<p className="mt-sm text-body-sm text-wi-text/80">{tier.tagline}</p>
								</div>
								<ul className="mt-md flex flex-1 flex-col gap-sm">{tier.bullets.map((b) => renderBullet(b))}</ul>
								<div className="mt-md pt-sm">
									{tier.id === "free" ? (
										<SecondaryLink to="/">{tier.ctaLabel}</SecondaryLink>
									) : null}
									{tier.id === "plus" ? (
										<PrimaryButton
											onClick={() => void startSupporterCheckout()}
											disabled={checkoutLoading}
										>
											{checkoutLoading ? "Opening Stripe..." : tier.ctaLabel}
										</PrimaryButton>
									) : null}
									{tier.id === "team" ? (
										<SecondaryLink href={CUSTOMER_CONTACT_MAILTO}>{tier.ctaLabel}</SecondaryLink>
									) : null}
								</div>
							</TierCard>
						))}
					</div>
				</div>
			</section>

			<section className="space-y-sm" aria-labelledby="team-roadmap-heading">
				<h2 id="team-roadmap-heading" className="wi-type-h2 text-wi-text">
					Team roadmap
				</h2>
				<p className="max-w-2xl text-body-sm text-wi-text/75">
					Beyond the one Planned line on the Team card, these capabilities stay in roadmap conversations — not the
					pricing grid.
				</p>
				<ul className="max-w-2xl space-y-xs text-body-sm text-wi-text/80">
					{TEAM_ROADMAP.map((item) => (
						<li key={item} className="flex gap-2.5">
							<span className="mt-2 shrink-0" aria-hidden>
								<svg width="7" height="7" viewBox="0 0 7 7" className="text-wi-gold" fill="none">
									<circle cx="3.5" cy="3.5" r="2.5" stroke="currentColor" strokeWidth="0.85" />
								</svg>
							</span>
							<span>{item}</span>
						</li>
					))}
				</ul>
			</section>

			<section
				className="wi-card p-8 md:p-10"
				aria-labelledby="supporter-boards-heading"
			>
				<p className="text-caption font-medium text-wi-gold uppercase tracking-[0.18em]">Plus administration</p>
				<h2 id="supporter-boards-heading" className="mt-sm wi-type-h2 text-wi-text">
					Reusable Ideas for recurring decisions
				</h2>
					<p className="mt-sm max-w-2xl text-body-sm text-wi-text/75">
						Supporter Ideas are designed for reusable prompts with editable answer templates, member controls, Open Ideas,
						Previous Ideas, and Reopen Idea workflows. Those admin capabilities are{" "}
						<strong className="text-wi-text">rolling out on staging</strong> after Stripe test-mode smoke and team
						bootstrap land. Voters can open <strong className="text-wi-text">team slug + Idea slug</strong> (for example{" "}
						<code className="rounded-md bg-wi-bg px-1.5 py-0.5 text-caption text-wi-text">worthyidea.net/acme/retro</code>)
						to land in the active room when an Idea is open.
					</p>
				<div className="mt-md flex flex-col gap-sm sm:flex-row sm:items-center">
					<SecondaryLink href={adminAppUrl("/")}>Manage Supporter Ideas</SecondaryLink>
					<p className="text-caption text-wi-text/58 leading-relaxed">
						Opens the admin console at admin.worthyidea.net (Cloudflare Access). Requires Plus checkout on the public
						app in the same browser so <code className="text-[11px]">wi_account</code> can reach the proxied API.
					</p>
				</div>
			</section>

			<section id="membership-checkout" className="wi-card scroll-mt-28 space-y-sm p-8 md:p-10">
				<h2 className="wi-type-h2 text-wi-text">Checkout</h2>
				<p className="max-w-2xl text-body-sm text-wi-text/75">
					Plus checkout uses a Stripe-hosted session when billing env is configured (test mode on staging; live
					payment on production when billing is unlocked). Your plan is applied only after the Worker verifies
					payment. If Stripe env is missing, Upgrade stays unavailable instead of taking payment.
				</p>
				<p className="text-body-sm text-wi-text/68">
					Need to talk through a team use case?{" "}
					<a href={CUSTOMER_CONTACT_MAILTO} className="font-medium text-wi-gold underline-offset-2 hover:underline">
						Email {CUSTOMER_CONTACT_EMAIL}
					</a>
					.
				</p>
			</section>

			<section className="space-y-md">
				<h2 className="wi-type-h2 text-wi-text">FAQ</h2>
				<dl className="space-y-md text-body-sm text-wi-text/80">
					<div>
						<dt className="font-medium text-wi-text">What is the Free plan?</dt>
						<dd className="mt-1 leading-relaxed">
							Our free <strong className="text-wi-text">Open</strong> line: ad-hoc polls with a 48-hour lifespan,
							public links, and no account wall for voters. Plus and Team add persistence and org tooling as those
							features ship.
						</dd>
					</div>
					<div>
						<dt className="font-medium text-wi-text">Ephemeral vs persistent?</dt>
						<dd className="mt-1 leading-relaxed">
							Phase 1 polls honor the time limit you set when you launch. Longer retention and tier-gated features
							ship with paid plans as billing and account features mature.
						</dd>
					</div>
					<div>
						<dt className="font-medium text-wi-text">What does Team SSO mean?</dt>
						<dd className="mt-1 leading-relaxed">
							Microsoft Entra ID (Azure AD) mapping for org-owned namespaces — planned for Team; not active in this
							build.
						</dd>
					</div>
				</dl>
			</section>

			<p className="text-body-sm text-wi-text/60">
				Curious about the math first?{" "}
				<Link to="/about" className="font-medium text-wi-gold underline-offset-2 hover:underline">
					About WorthyIdea
				</Link>
			</p>
		</div>
	);
}
